/**
 * What an aged care assessment actually decides — and why one of the two answers is not something
 * we can cost.
 *
 * WHY THIS EXISTS (owner, 2026-08-28). The app had nothing to say about the Commonwealth Home
 * Support Program, the entry-level program roughly 830,000 older Australians actually use. A client
 * who has not been assessed was told only that we could not find their Support at Home
 * classification, and invited to pick one.
 *
 * THE LINE THIS COMPONENT HOLDS. CHSP has NO MEANS TEST — no income test, no assets test — and each
 * provider sets its own fees. There is therefore no formula to model, and we must not invent one:
 * a number we produced would be a guess wearing the costume of an answer. So this explains, and
 * deliberately does not calculate. The honest reason is stated to the client rather than hidden,
 * because "we don't model this, and here is why" is evidence that our other numbers are real.
 *
 * WHAT IT DELIBERATELY DOES NOT DO. It does not predict which way a given client will fall. The
 * owner's first instinct was to infer the likely outcome from the health question — "you told us
 * you're in good health, so you may only need CHSP". Good general health does not mean low care
 * needs (a fall, mobility, cognition and isolation all drive needs up while someone still reports
 * good health), and a wrong guess in the app's voice could talk a client out of an assessment they
 * would have qualified for. Explaining both outcomes is more useful and cannot misfire.
 *
 * Facts sourced from the Department of Health, Disability and Ageing; the CHSP extension to
 * 30 June 2029 and the confirmation that it stays standalone were announced 20 August 2026. The
 * same facts are in the AWise knowledge base (awiseKnowledgeBase.ts, concept `chsp`) so the
 * assistant and the screens cannot disagree.
 */
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Info, X, CheckCircle2 } from 'lucide-react';
import { InsightCard } from '../ui/index.tsx';

/** The two outcomes, in the client's words. Shared by the fork panel and the comparison table. */
const PATHS = {
    chsp: {
        title: 'Entry-level support (CHSP)',
        who: 'Help around the home — cleaning, meals, transport, social support',
        points: [
            <>Fees are <strong>set by each provider</strong></>,
            <><strong>No income or assets test</strong></>,
            <>Nobody is refused a service for being unable to pay</>,
        ],
    },
    sah: {
        title: 'Support at Home (Levels 1–8)',
        who: 'An ongoing budget for assessed, continuing care needs',
        points: [
            <>You get an <strong>individual yearly budget</strong></>,
            <>Your contribution <strong>is means tested</strong></>,
            <>This is the one we can model for you</>,
        ],
    },
} as const;

const PathCard = ({ path, accent }: { path: typeof PATHS[keyof typeof PATHS]; accent: string }) => (
    <div className={`rounded-xl border border-slate-200/90 bg-white p-4 border-t-[3px] ${accent}`}>
        <h5 className="text-[15px] font-bold text-gray-800 mb-0.5">{path.title}</h5>
        <p className="text-xs text-slate-500 mb-2.5">{path.who}</p>
        <ul className="list-disc pl-4 space-y-0.5 text-[13.5px] text-gray-700 leading-relaxed">
            {path.points.map((point, i) => <li key={i}>{point}</li>)}
        </ul>
    </div>
);

interface AssessmentForkNoteProps {
    /** The person this is about — "Baldo", or "you" in first-person voice. */
    who?: string;
    /** Opens the full comparison. */
    onCompare: () => void;
    className?: string;
}

/**
 * Shown where a client says they have NOT been assessed. Names the two outcomes and their cost
 * shapes, so the reader meets the idea before the Support at Home module rather than after it.
 */
export function AssessmentForkNote({ who, onCompare, className = '' }: AssessmentForkNoteProps) {
    return (
        <InsightCard tone="info" heading="What the assessment decides" icon={Info} className={className}>
            <p className="text-sm text-gray-700 leading-relaxed mb-3">
                An assessment sorts {who ? <strong>{who}</strong> : 'people'} onto <strong>one of two
                paths</strong>, and they cost very different things:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <PathCard path={PATHS.chsp} accent="border-t-emerald-400" />
                <PathCard path={PATHS.sah} accent="border-t-brand-blue" />
            </div>
            <p className="text-sm text-gray-700 mt-3">
                <button type="button" onClick={onCompare} className="font-semibold text-blue-600 underline decoration-blue-600/40 underline-offset-2 hover:text-blue-700">
                    See a full comparison
                </button>
                <span className="text-slate-500">
                    {' '}· We can&apos;t know which {who || 'you'} would be assessed for — only the assessment decides that.
                </span>
            </p>
        </InsightCard>
    );
}

const ROWS: Array<{ label: string; chsp: React.ReactNode; sah: React.ReactNode; key?: boolean }> = [
    {
        label: "Who it's for",
        chsp: 'Lower needs — help to keep managing at home',
        sah: 'Assessed, ongoing care needs',
    },
    {
        label: 'What you get',
        chsp: 'Individual services: cleaning, meals, transport, social support, minor home maintenance',
        sah: 'A yearly budget you spend across care, independence and everyday living services',
    },
    {
        label: "How it's funded",
        chsp: <>Government funds the <em>provider</em> directly</>,
        sah: <>Government allocates a budget to <em>you</em>, at Levels 1–8</>,
    },
    {
        label: 'Is it means tested?',
        chsp: 'No — no income test, no assets test',
        sah: 'Yes — your contribution depends on your income and assets',
        key: true,
    },
    {
        label: 'What you pay',
        chsp: 'A fee set by each provider, based on what you can afford. You cannot be refused a service because you can’t pay.',
        sah: 'A contribution that varies by service type and your means-tested category',
    },
    {
        label: 'Can AgedCareWise model it?',
        /*
         * Unusual on a client-facing table, and kept deliberately. It answers the question the
         * reader is already forming, and giving the REASON turns a limitation into evidence that we
         * do not make numbers up.
         */
        chsp: 'No — and that’s not a gap. There’s no formula: fees differ provider to provider, so any figure we produced would be a guess.',
        sah: 'Yes — this is what the module is built for',
        key: true,
    },
    {
        label: 'How long does it run?',
        chsp: <>Funded to <strong>30 June 2029</strong>, and the government has confirmed it stays a standalone program</>,
        sah: 'Ongoing — replaced Home Care Packages on 1 November 2025',
    },
];

interface ChspComparisonModalProps {
    open: boolean;
    onClose: () => void;
}

/**
 * The full side-by-side. Rendered in a portal so it is never clipped by an ancestor's overflow —
 * this opens from inside a fact-find step and from inside the Support at Home page, and neither
 * layout can be assumed to allow an overlay to escape.
 */
export function ChspComparisonModal({ open, onClose }: ChspComparisonModalProps) {
    // Escape closes, and the page behind does not scroll while it is open.
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = previousOverflow;
        };
    }, [open, onClose]);

    if (!open || typeof document === 'undefined') return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-brand-ink/55 p-4 sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="chsp-compare-title"
            onClick={onClose}
        >
            <div
                className="my-4 w-full max-w-3xl rounded-2xl border border-slate-200/70 bg-white p-6 shadow-cardlg sm:p-7"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-4 flex items-start justify-between gap-4">
                    <div>
                        <h3 id="chsp-compare-title" className="text-[23px] font-bold leading-tight">
                            <span className="bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text text-transparent">
                                Entry-level support vs Support at Home
                            </span>
                        </h3>
                        <p className="mt-0.5 text-sm text-gray-600">
                            The two things an assessment can decide — and what each costs you.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close comparison"
                        className="mt-1 shrink-0 rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    >
                        <X size={20} strokeWidth={2.4} />
                    </button>
                </div>

                {/* Its own scroller: a seven-row comparison must never make the page scroll sideways. */}
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[600px] table-fixed border-collapse text-sm">
                        <colgroup>
                            <col className="w-[26%]" />
                            <col className="w-[37%]" />
                            <col className="w-[37%]" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th className="border-b-2 border-slate-200 px-3 py-3" />
                                <th className="border-b-2 border-slate-200 px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                                    Entry-level support (CHSP)
                                </th>
                                <th className="border-b-2 border-slate-200 px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                                    Support at Home
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {ROWS.map((row) => (
                                <tr key={row.label} className={row.key ? 'bg-blue-50' : ''}>
                                    <th scope="row" className={`w-[26%] border-b border-slate-200 px-3 py-3 text-left align-top font-bold text-gray-800 ${row.key ? '' : 'bg-slate-50/60'}`}>
                                        {row.label}
                                    </th>
                                    <td className={`border-b border-slate-200 px-3 py-3 align-top leading-relaxed ${row.key ? 'font-bold text-brand-navy' : 'text-gray-700'}`}>
                                        {row.chsp}
                                    </td>
                                    <td className={`border-b border-slate-200 px-3 py-3 align-top leading-relaxed ${row.key ? 'font-bold text-brand-navy' : 'text-gray-700'}`}>
                                        {row.sah}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/*
                 * Stops the table reading as bad news. A CHSP outcome is financially BETTER for the
                 * client — nothing they own or earn changes what they pay — and they should leave
                 * knowing that rather than feeling they missed out on the modelled option.
                 */}
                <InsightCard tone="positive" icon={CheckCircle2} className="mt-5">
                    <p className="text-sm text-gray-700 leading-relaxed">
                        <strong>If entry-level support is where you land, that&apos;s good news financially.</strong>{' '}
                        There&apos;s no means test, so nothing about your income or assets changes what you pay —
                        and the planning we do elsewhere in AgedCareWise still applies in full.
                    </p>
                </InsightCard>

                <p className="mt-3.5 text-[11.5px] text-slate-400">
                    Source: Department of Health, Disability and Ageing — CHSP extended to 30 June 2029
                    (announced 20 August 2026).
                </p>
            </div>
        </div>,
        document.body,
    );
}

export default AssessmentForkNote;
